import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { 
  Clock, FileText, Camera, ExternalLink, ArrowRight, 
  ChevronRight, Check, AlertCircle, Globe, DollarSign, Calendar,
  Plane, Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VisaDetailsPage = () => {
  const { countryCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const originCountry = searchParams.get('origin');
  
  const [countryData, setCountryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVisaType, setSelectedVisaType] = useState(null);

  useEffect(() => {
    fetchCountryDetails();
  }, [countryCode]);

  const fetchCountryDetails = async () => {
    try {
      const response = await axios.get(`${API}/countries/${countryCode}`);
      setCountryData(response.data);
      if (response.data.visa_types?.length > 0) {
        setSelectedVisaType(response.data.visa_types[0]);
      }
    } catch (error) {
      console.error("Error fetching country details:", error);
      toast.error("Failed to load country details");
    } finally {
      setLoading(false);
    }
  };

  const getProcessingClass = (days) => {
    if (days <= 7) return "text-emerald-600 bg-emerald-100";
    if (days <= 21) return "text-amber-600 bg-amber-100";
    return "text-red-600 bg-red-100";
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#E15636] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748B]">Loading visa information...</p>
        </div>
      </div>
    );
  }

  if (!countryData) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-heading text-[#0F172A] mb-2">Country Not Found</h2>
          <p className="text-[#64748B] mb-6">The requested country information is not available.</p>
          <Link to="/visa-finder">
            <Button className="rounded-full bg-[#0F172A]">Back to Visa Finder</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 md:pt-32 pb-20" data-testid="visa-details-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-8">
        <div className="flex items-center gap-2 text-sm text-[#64748B]">
          <Link to="/" className="hover:text-[#0F172A]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/visa-finder" className="hover:text-[#0F172A]">Visa Finder</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#0F172A] font-medium">{countryData.country_name}</span>
        </div>
      </div>

      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
        <div className="flex flex-col md:flex-row items-start gap-6">
          <span className="text-7xl md:text-8xl">{countryData.flag}</span>
          <div className="flex-1">
            <h1 className="text-4xl md:text-6xl font-heading tracking-tight text-[#0F172A] mb-4">
              {countryData.country_name} <span className="text-[#E15636]">Visa</span>
            </h1>
            <div className="flex flex-wrap items-center gap-4">
              {countryData.is_visa_on_arrival && (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 px-4 py-2">
                  <Check className="w-4 h-4 mr-1" />
                  Visa on Arrival Available
                </Badge>
              )}
              <a 
                href={countryData.embassy_website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#E15636] hover:underline"
                data-testid="embassy-link"
              >
                <Globe className="w-4 h-4" />
                Official Embassy Website
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Visa on Arrival Info */}
      {countryData.is_visa_on_arrival && countryData.visa_on_arrival_info && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6" data-testid="voa-info">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Plane className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-heading text-emerald-800 mb-2">Visa on Arrival Information</h3>
                <p className="text-emerald-700 leading-relaxed">{countryData.visa_on_arrival_info}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Visa Types & Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Visa Types */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8" data-testid="visa-types">
              <h2 className="text-2xl font-heading text-[#0F172A] mb-6">Available Visa Types</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {countryData.visa_types?.map((visa) => (
                  <button
                    key={visa.type_code}
                    onClick={() => setSelectedVisaType(visa)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${
                      selectedVisaType?.type_code === visa.type_code
                        ? 'border-[#E15636] bg-[#E15636]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`visa-type-${visa.type_code}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-[#0F172A]">{visa.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProcessingClass(visa.processing_days)}`}>
                        {visa.processing_days} days
                      </span>
                    </div>
                    <p className="text-sm text-[#64748B] mb-3 line-clamp-2">{visa.description}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#64748B]">
                        <DollarSign className="w-3 h-3 inline" />
                        CAD ${visa.price_cad}
                      </span>
                      <span className="text-[#64748B]">{visa.validity}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Selected Visa Details */}
            {selectedVisaType && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8" data-testid="selected-visa-details">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-heading text-[#0F172A] mb-2">{selectedVisaType.name}</h2>
                    <p className="text-[#64748B]">{selectedVisaType.description}</p>
                  </div>
                  <a 
                    href={countryData.official_visa_portal || countryData.embassy_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="apply-official-btn"
                  >
                    <Button className="rounded-full bg-[#E15636] hover:bg-[#C24125]">
                      Apply on Official Site
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
                      <DollarSign className="w-4 h-4" />
                      Fee
                    </div>
                    <p className="text-xl font-heading text-[#0F172A]">CAD ${selectedVisaType.price_cad}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
                      <Clock className="w-4 h-4" />
                      Processing
                    </div>
                    <p className="text-xl font-heading text-[#0F172A]">{selectedVisaType.processing_days} days</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      Validity
                    </div>
                    <p className="text-xl font-heading text-[#0F172A]">{selectedVisaType.validity}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
                      <Plane className="w-4 h-4" />
                      Entries
                    </div>
                    <p className="text-xl font-heading text-[#0F172A]">{selectedVisaType.entries}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs for Documents, Photo Specs, Notes */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <Tabs defaultValue="documents" className="w-full">
                <TabsList className="w-full justify-start border-b border-slate-200 rounded-none bg-slate-50 p-0 h-auto">
                  <TabsTrigger 
                    value="documents" 
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E15636] data-[state=active]:bg-white px-6 py-4"
                    data-testid="tab-documents"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Documents
                  </TabsTrigger>
                  <TabsTrigger 
                    value="photo" 
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E15636] data-[state=active]:bg-white px-6 py-4"
                    data-testid="tab-photo"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Photo Specs
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notes" 
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E15636] data-[state=active]:bg-white px-6 py-4"
                    data-testid="tab-notes"
                  >
                    <Info className="w-4 h-4 mr-2" />
                    Notes
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="documents" className="p-6 md:p-8 mt-0">
                  <div className="space-y-4" data-testid="documents-list">
                    {countryData.documents?.map((doc, index) => (
                      <div 
                        key={index}
                        className={`flex items-start gap-4 p-4 rounded-xl bg-slate-50 border ${
                          doc.mandatory ? 'border-l-4 border-l-[#E15636] border-slate-200' : 'border-slate-200'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          doc.mandatory ? 'bg-[#E15636] text-white' : 'bg-slate-200 text-slate-600'
                        }`}>
                          <Check className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="font-medium text-[#0F172A] mb-1">
                            {doc.name}
                            {doc.mandatory && <span className="text-red-500 ml-1">*</span>}
                          </h4>
                          <p className="text-sm text-[#64748B]">{doc.description}</p>
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-[#64748B] mt-4">* Required documents</p>
                  </div>
                </TabsContent>

                <TabsContent value="photo" className="p-6 md:p-8 mt-0">
                  {countryData.photo_spec && (
                    <div className="photo-spec-box" data-testid="photo-specs">
                      <div className="flex items-start gap-6">
                        <div className="w-24 h-32 bg-white border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
                          <Camera className="w-8 h-8 text-slate-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-heading text-[#0F172A] mb-4">Photo Requirements</h3>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-sm text-[#64748B]">Width</p>
                              <p className="font-medium text-[#0F172A]">{countryData.photo_spec.width}</p>
                            </div>
                            <div>
                              <p className="text-sm text-[#64748B]">Height</p>
                              <p className="font-medium text-[#0F172A]">{countryData.photo_spec.height}</p>
                            </div>
                            <div>
                              <p className="text-sm text-[#64748B]">Background</p>
                              <p className="font-medium text-[#0F172A]">{countryData.photo_spec.background}</p>
                            </div>
                            <div>
                              <p className="text-sm text-[#64748B]">Format</p>
                              <p className="font-medium text-[#0F172A]">{countryData.photo_spec.format}</p>
                            </div>
                          </div>
                          {countryData.photo_spec.additional_notes?.length > 0 && (
                            <div>
                              <p className="text-sm text-[#64748B] mb-2">Additional Requirements:</p>
                              <ul className="space-y-1">
                                {countryData.photo_spec.additional_notes.map((note, index) => (
                                  <li key={index} className="flex items-start gap-2 text-sm text-[#0F172A]">
                                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    {note}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="notes" className="p-6 md:p-8 mt-0">
                  <div className="space-y-4" data-testid="notes-list">
                    {countryData.notes?.map((note, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl"
                      >
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">{note}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Right Column - Quick Actions */}
          <div className="space-y-8">
            {/* Apply Now Card */}
            <div className="bg-[#0F172A] rounded-2xl p-6 text-white" data-testid="apply-card">
              <h3 className="text-lg font-heading mb-4">Ready to Apply?</h3>
              <p className="text-slate-300 text-sm mb-6">
                Apply for your visa through the official {countryData.country_name} government portal.
              </p>
              <a 
                href={countryData.official_visa_portal || countryData.embassy_website}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid="apply-portal-link"
              >
                <Button className="w-full rounded-full bg-[#E15636] hover:bg-[#C24125]">
                  Official Visa Portal
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="quick-actions">
              <h3 className="text-lg font-heading text-[#0F172A] mb-4">Quick Links</h3>
              <div className="space-y-3">
                <a 
                  href={countryData.embassy_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-slate-100 text-[#0F172A] rounded-xl hover:bg-slate-200 transition-colors"
                  data-testid="quick-embassy"
                >
                  <span className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Embassy Website
                  </span>
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Link 
                  to="/terms"
                  className="flex items-center justify-between p-4 bg-slate-100 text-[#0F172A] rounded-xl hover:bg-slate-200 transition-colors"
                  data-testid="quick-terms"
                >
                  <span className="flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Terms & Conditions
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6" data-testid="disclaimer">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800 mb-2">Educational Purpose Only</h4>
                  <p className="text-sm text-amber-700 leading-relaxed">
                    This information is provided for educational purposes. Always apply through official 
                    embassy or consulate websites. We do not process visa applications.
                  </p>
                  <Link 
                    to="/terms"
                    className="inline-flex items-center gap-1 text-sm text-amber-800 font-medium mt-2 hover:underline"
                  >
                    Read Full Terms
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisaDetailsPage;
